import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { refreshAccessToken } from '../utils/wsTokenRefresh';

const LiveQueueContext = createContext({ queueCount: 0, queueCalls: [], connected: false });

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL;
const MAX_RECONNECT_ATTEMPTS = 5;

/**
 * Provides real-time call queue data from the ws/live/ WebSocket.
 * One connection shared by all consumers (CompaignModule KPI card, etc.)
 * Connects only after login via authStateChange (no polling).
 */
export function LiveQueueProvider({ children }) {
  const [queueCalls, setQueueCalls] = useState([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const hasInitializedRef = useRef(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;
      socketRef.current.onclose = null;
      if (
        socketRef.current.readyState === WebSocket.OPEN ||
        socketRef.current.readyState === WebSocket.CONNECTING
      ) {
        socketRef.current.close(1000, 'Cleanup');
      }
      socketRef.current = null;
    }
    setConnected(false);
  }, []);

  const scheduleReconnect = useCallback((connectWebSocket, delayMs) => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    reconnectTimeoutRef.current = setTimeout(connectWebSocket, delayMs);
  }, []);

  const connectWebSocket = useCallback(() => {
    const token = sessionStorage.getItem('access_token');
    const role = sessionStorage.getItem('user_role');
    // Superadmin has no org-level queue — skip connection entirely
    if (!token || !SOCKET_URL || role === 'superadmin') return;

    const state = socketRef.current?.readyState;
    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) return;

    try {
      const wsUrl = `${SOCKET_URL}/ws/live/?token=${token}`;
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptsRef.current = 0;
        setConnected(true);
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'call_update' && Array.isArray(data.calls)) {
            setQueueCalls(data.calls);
          }
        } catch (_) {}
      };

      socket.onerror = () => {};

      socket.onclose = async (event) => {
        socketRef.current = null;
        setConnected(false);

        if (event.code === 4001 || event.code === 4002) {
          reconnectAttemptsRef.current = 0;
          const newToken = await refreshAccessToken();
          if (newToken) {
            scheduleReconnect(connectWebSocket, 1000);
            return;
          }
          return;
        }

        if (event.code === 1000 || event.code === 1001) return;
        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) return;

        reconnectAttemptsRef.current += 1;
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        scheduleReconnect(connectWebSocket, delay);
      };
    } catch (_) {}
  }, [scheduleReconnect]);

  useEffect(() => {
    const checkAndConnect = () => {
      const token = sessionStorage.getItem('access_token');
      if (!token) return;
      if (hasInitializedRef.current) return;
      hasInitializedRef.current = true;
      reconnectAttemptsRef.current = 0;
      connectWebSocket();
    };

    checkAndConnect();

    const handleAuthChange = () => {
      cleanup();
      hasInitializedRef.current = false;
      reconnectAttemptsRef.current = 0;
      setQueueCalls([]);
      setTimeout(checkAndConnect, 500);
    };

    window.addEventListener('authStateChange', handleAuthChange);

    return () => {
      window.removeEventListener('authStateChange', handleAuthChange);
      cleanup();
      hasInitializedRef.current = false;
    };
  }, [connectWebSocket, cleanup]);

  const queueCount = queueCalls.length;

  return (
    <LiveQueueContext.Provider value={{ queueCount, queueCalls, connected }}>
      {children}
    </LiveQueueContext.Provider>
  );
}

export function useLiveQueue() {
  return useContext(LiveQueueContext);
}
